import { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import BodyField from '../components/BodyField';
import { richTextAllowed } from '../utils/richText';
import {
  getVacancies, createVacancy, updateVacancy, deleteVacancy, Vacancy,
  getTeams, Team,
} from '../api/client';
import './Vacancies.css';

/**
 * Volunteer vacancies — the roles the club is trying to fill, published at
 * /volunteer on the public site. Club + Pro; the server 404s Free, so this
 * page showing is a prompt and never the gate.
 */
const BLANK = {
  title: '', description: '', teamId: '', commitment: '',
  availableFrom: '', contactEmail: '', status: 'open' as const, order: 0,
};

export default function Vacancies() {
  const { plan, features } = useAuth();
  const isPaid = plan === 'club' || plan === 'pro';
  const [rows, setRows] = useState<Vacancy[]>([]);
  const [teams, setTeams] = useState<Team[]>([]);
  const [form, setForm] = useState<typeof BLANK>({ ...BLANK });
  const [editing, setEditing] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const load = () => getVacancies().then(setRows).catch(() => setRows([]));
  useEffect(() => { if (isPaid) { load(); getTeams().then(setTeams).catch(() => {}); } }, [isPaid]);

  const reset = () => { setForm({ ...BLANK }); setEditing(null); setError(''); };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.title.trim()) { setError('Give the role a title.'); return; }
    setBusy(true); setError('');
    try {
      if (editing) await updateVacancy(editing, form);
      else await createVacancy(form);
      reset(); await load();
    } catch (err: any) {
      setError(err?.message || 'Could not save that.');
    } finally { setBusy(false); }
  };

  const edit = (v: Vacancy) => {
    setEditing(v.id);
    setForm({
      title: v.title || '', description: v.description || '', teamId: v.teamId || '',
      commitment: v.commitment || '', availableFrom: v.availableFrom || '',
      contactEmail: v.contactEmail || '', status: (v.status || 'open') as 'open',
      order: Number(v.order ?? 0),
    });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const remove = async (v: Vacancy) => {
    if (!window.confirm(`Delete "${v.title}"? If the role is filled, mark it filled instead — it stays for next season.`)) return;
    await deleteVacancy(v.id); await load();
  };

  if (!isPaid) {
    return (
      <div className="page">
          <div className="empty-state">
          <h2>Volunteer vacancies</h2>
          <p>Advertise the roles your club needs filling on a public page people can be sent to. Available on the Club and Pro plans.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="page vacancies-page">
      <div className="page-header">
        <h1 className="page-title">Volunteer vacancies</h1>
        <p className="page-subtitle">These appear on your public <strong>/volunteer</strong> page. Filled roles are hidden there but kept here for next season.</p>
      </div>

      <form className="card vac-form" onSubmit={submit}>
        <h2 className="card-title">{editing ? 'Edit role' : 'Add a role'}</h2>
        {error && <div className="error-message">{error}</div>}

        <div className="form-group">
          <label className="form-label" htmlFor="v-title">Role title</label>
          <input id="v-title" className="form-input" maxLength={80} value={form.title}
            onChange={e => setForm({ ...form, title: e.target.value })}
            placeholder="e.g. Under-6s Coach, Club Secretary, Fundraising committee" />
        </div>

        <div className="form-group">
          <label className="form-label" htmlFor="v-desc">What's involved</label>
          <BodyField id="v-desc" className="form-input" rows={4} maxLength={1200} value={form.description}
            rich={richTextAllowed(plan, features)} compact
            onChange={description => setForm({ ...form, description })}
            placeholder="What the role actually means week to week. Say if no experience is needed — most people assume it is." />
        </div>

        <div className="vac-row">
          <div className="form-group">
            <label className="form-label" htmlFor="v-team">Team (optional)</label>
            <select id="v-team" className="form-input" value={form.teamId}
              onChange={e => setForm({ ...form, teamId: e.target.value })}>
              <option value="">Club-wide</option>
              {teams.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select>
          </div>
          <div className="form-group">
            <label className="form-label" htmlFor="v-commit">Commitment</label>
            <input id="v-commit" className="form-input" maxLength={120} value={form.commitment}
              onChange={e => setForm({ ...form, commitment: e.target.value })}
              placeholder="e.g. 2 hours a week, Saturday mornings" />
          </div>
        </div>

        <div className="vac-row">
          <div className="form-group">
            <label className="form-label" htmlFor="v-from">Available from</label>
            <input id="v-from" className="form-input" maxLength={120} value={form.availableFrom}
              onChange={e => setForm({ ...form, availableFrom: e.target.value })}
              placeholder="e.g. Now, or End of the season" />
          </div>
          <div className="form-group">
            <label className="form-label" htmlFor="v-email">Contact email (optional)</label>
            <input id="v-email" type="email" className="form-input" maxLength={120} value={form.contactEmail}
              onChange={e => setForm({ ...form, contactEmail: e.target.value })}
              placeholder="Leave blank to use your contact form" />
          </div>
        </div>

        <div className="form-group">
          <label className="form-label" htmlFor="v-status">Status</label>
          <select id="v-status" className="form-input" value={form.status}
            onChange={e => setForm({ ...form, status: e.target.value as 'open' })}>
            <option value="open">Open — show on the website</option>
            <option value="filled">Filled — hide it, but keep it</option>
          </select>
        </div>

        <div className="vac-actions">
          <button className="btn btn-primary" disabled={busy}>{busy ? 'Saving…' : editing ? 'Save changes' : 'Add role'}</button>
          {editing && <button type="button" className="btn btn-secondary" onClick={reset}>Cancel</button>}
        </div>
      </form>

      {rows.length > 0 && (
        <div className="card">
          <h2 className="card-title">{rows.length} role{rows.length === 1 ? '' : 's'}</h2>
          <ul className="vac-list">
            {rows.map(v => (
              <li key={v.id} className={`vac-item${v.status === 'filled' ? ' is-filled' : ''}`}>
                <div>
                  <strong>{v.title}</strong>
                  {v.status === 'filled' && <span className="vac-tag">Filled</span>}
                  {v.availableFrom && <div className="vac-meta">From {v.availableFrom}</div>}
                </div>
                <div className="vac-item-actions">
                  <button className="btn btn-secondary btn-sm" onClick={() => edit(v)}>Edit</button>
                  <button className="btn btn-danger btn-sm" onClick={() => remove(v)}>Delete</button>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
